import { Job } from '@cibli/shared';
import { Button, Card } from '@cibli/shared';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ContractTypeBadge } from '@/components/molecules/ContractTypeBadge';
import { EmploymentTypeBadge } from '@/components/molecules/EmploymentTypeBadge';
import { formatDate } from '@/utils/dateFormatters';

export type JobDetailContentProps = {
  job: Job;
  showCard?: boolean;
  className?: string;
  isApplying?: boolean;
  hasApplied?: boolean;
  handleApplyClick?: () => void;
};

/**
 * Job detail content component (Molecule).
 * Displays detailed job information in the jobs list panel.
 */
export const JobDetailContent = ({
  job,
  showCard = true,
  className = '',
  isApplying = false,
  hasApplied = false,
  handleApplyClick = () => {},
}: JobDetailContentProps) => {
  const { t, i18n } = useTranslation();

  const content = (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="text-lg">{job.companyName}</span>
        {job.updatedAt && (
          <span className="text-gray-500">{formatDate(job.updatedAt, i18n.language)}</span>
        )}
      </div>
      <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold mt-4">{job.title}</h1>
      <div className="flex flex-row gap-2 mt-2 flex-wrap">
        {job.contractType && <ContractTypeBadge contractType={job.contractType} />}
        {job.employmentTypes?.map((type: string) => (
          <EmploymentTypeBadge key={type} employmentType={type} />
        ))}
      </div>
      {job.expectedStartDate && (
        <span className="text-primary mt-4">
          {t('MatchingJobs.starting', { date: job.expectedStartDate })}
        </span>
      )}

      <div className="">
        <Button onClick={handleApplyClick} disabled={isApplying || hasApplied}>
          {hasApplied ? t('dashboard.jobs.alreadyApplied') : t('MatchingJobs.apply')}
          {isApplying && <Loader2 className="w-4 h-4 animate-spin" />}
        </Button>
      </div>

      <div className="flex flex-row gap-2 p-2">
        <div className="flex flex-col gap-2">
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description ?? '') }} />
        </div>
      </div>

      {/* Informations ROME, Qualification et Secteur d'activité */}
      {(job.romeLibelle || job.qualificationLibelle || job.secteurActiviteLibelle) && (
        <div className="flex flex-col gap-2 mt-4">
          {/* Informations ROME */}
          {job.romeLibelle && (
            <div className="text-sm text-muted-foreground">
              {job.romeLibelle}
              {job.romeCode && (
                <>
                  {' ('}
                  {job.romeCode}
                  {job.appellationLibelle && ` / ${job.appellationLibelle}`}
                  {')'}
                </>
              )}
            </div>
          )}
          {/* Qualification */}
          {job.qualificationLibelle && (
            <div className="text-sm text-muted-foreground">
              {job.qualificationLibelle}
              {job.qualificationCode && ` (${job.qualificationCode})`}
            </div>
          )}
          {/* Secteur d'activité */}
          {job.secteurActiviteLibelle && (
            <div className="text-sm text-muted-foreground">{job.secteurActiviteLibelle}</div>
          )}
        </div>
      )}

      <div className="w-full h-[1px] bg-gray-200 my-4" />

      {(() => {
        if (!job.salary) return null;

        const hasSalaryLabel = job.salary.libelle && job.salary.libelle.trim();
        const hasComplement1 = !!job.salary.complement1;
        const hasComplement2 = !!job.salary.complement2;
        const hasListeComplements =
          job.salary.listeComplements && job.salary.listeComplements.length > 0;
        const hasComplements = hasComplement1 || hasComplement2 || hasListeComplements;
        const shouldShowSalary = hasSalaryLabel || hasComplements;

        if (!shouldShowSalary) return null;

        return (
          <div className="flex flex-row gap-2 p-2">
            <div className="flex flex-col gap-2">
              {hasSalaryLabel && <h2 className="text-2xl">{job.salary.libelle}</h2>}
              {hasComplements && (
                <div className="flex flex-col gap-1 mt-2">
                  {hasComplement1 && (
                    <span className="text-sm text-muted-foreground">{job.salary.complement1}</span>
                  )}
                  {hasComplement2 && (
                    <span className="text-sm text-muted-foreground">{job.salary.complement2}</span>
                  )}
                  {hasListeComplements &&
                    job.salary.listeComplements &&
                    job.salary.listeComplements
                      .filter(
                        comp =>
                          comp.libelle !== job.salary?.complement1 &&
                          comp.libelle !== job.salary?.complement2,
                      )
                      .map((comp, index) => (
                        <span key={index} className="text-sm text-muted-foreground">
                          {comp.libelle}
                        </span>
                      ))}
                </div>
              )}
              <span className="text-gray-500">{job.city}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );

  if (!showCard) {
    return content;
  }

  return <Card className="p-8 flex-1">{content}</Card>;
};
